import { useEffect, useMemo, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useTenant } from '../contexts/TenantContext';
import { secondsToHours } from '../lib/formatHelpers';

/**
 * useCreatorDetail — fetches a single creator + its current-month snapshot +
 * the last `lookbackDays` of metrics (default 60).
 *
 * R1 : tenant_id + agency_id filter.
 * R10 : daily metrics paginated 1000 rows (a single creator over 60 days < 100
 *       rows so the loop is largely cosmetic, but the pattern is enforced).
 *
 * Params :
 *  - creatorId    : uuid of the creator row
 *  - lookbackDays : default 60
 *
 * Returns :
 *  - creator      : creators row (extended with manager_name)
 *  - currentMonth : creator_current_month row
 *  - dailyRows    : array of creator_metrics_daily over the lookback
 *  - activity     : { sessions, hours, days, validGoLiveDays, matches,
 *                     newFollowers } aggregated over the lookback
 *  - loading / error / reload
 */
export function useCreatorDetail({ creatorId, lookbackDays = 60 } = {}) {
  const { tenantId, agencyId, loading: tenantLoading } = useTenant();
  const [creator, setCreator] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(null);
  const [dailyRows, setDailyRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!tenantId || !creatorId) return;
    setLoading(true);
    setError(null);
    try {
      // 1) creator row
      let q1 = supabase
        .from('black_creators')
        .select('id, display_name, tiktok_username, tiktok_id, avatar_url, server, tier, status, last_live_date, valid_days, whatsapp_phone, manager_id, contract_end_date, note, created_at, tenant_id, agency_id')
        .eq('tenant_id', tenantId)
        .eq('id', creatorId);
      if (agencyId) q1 = q1.eq('agency_id', agencyId);
      const { data: creatorData, error: e1 } = await q1.maybeSingle();
      if (e1) throw e1;
      if (!creatorData) {
        setCreator(null);
        setLoading(false);
        return;
      }

      // 2) manager name (if any)
      let managerName = null;
      if (creatorData.manager_id) {
        const { data: m } = await supabase
          .from('profiles')
          .select('full_name, email')
          .eq('id', creatorData.manager_id)
          .maybeSingle();
        managerName = m?.full_name || m?.email || null;
      }

      // 3) current month snapshot
      const { data: cm } = await supabase
        .from('black_creator_current_month')
        .select('creator_id, tier_current, tier_last_month, forecasted_tier, days_since_joining, status, server')
        .eq('tenant_id', tenantId)
        .eq('creator_id', creatorId)
        .maybeSingle();

      // 4) daily rows over lookback (paginated R10)
      const today = new Date();
      const since = new Date(today);
      since.setUTCDate(today.getUTCDate() - lookbackDays);
      const sinceStr = since.toISOString().slice(0, 10);
      const PAGE = 1000;
      const all = [];
      let from = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        let q = supabase
          .from('black_creator_daily')
          .select('snapshot_date, live_streams, live_duration_seconds, valid_go_live_days, matches, new_followers')
          .eq('tenant_id', tenantId)
          .eq('creator_id', creatorId)
          .gte('snapshot_date', sinceStr)
          .order('snapshot_date', { ascending: false });
        if (agencyId) q = q.eq('agency_id', agencyId);
        const { data, error: e4 } = await q.range(from, from + PAGE - 1);
        if (e4) throw e4;
        const rows = data || [];
        all.push(...rows);
        if (rows.length < PAGE) break;
        from += PAGE;
      }

      setCreator({ ...creatorData, manager_name: managerName });
      setCurrentMonth(cm || null);
      setDailyRows(all);
    } catch (e) {
      console.error('[useCreatorDetail] error:', e);
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [tenantId, agencyId, creatorId, lookbackDays]);

  useEffect(() => {
    if (!tenantLoading) fetchAll();
  }, [fetchAll, tenantLoading]);

  const activity = useMemo(() => {
    let sessions = 0, hours = 0, days = 0, validDays = 0, matches = 0, newFollowers = 0;
    for (const r of dailyRows) {
      sessions += r.live_streams || 0;
      hours += secondsToHours(r.live_duration_seconds || 0);
      if (r.live_streams > 0 || r.valid_go_live_days > 0) days += 1;
      validDays += r.valid_go_live_days || 0;
      matches += r.matches || 0;
      newFollowers += r.new_followers || 0;
    }
    return { sessions, hours, days, validGoLiveDays: validDays, matches, newFollowers };
  }, [dailyRows]);

  return { creator, currentMonth, dailyRows, activity, loading: loading || tenantLoading, error, reload: fetchAll };
}
